// Whether anything in the app is currently guarding the page against being unloaded.
//
// StaleBundleGuard reloads silently, and a reload fires `beforeunload`. Any flow that has
// registered a beforeunload handler would therefore turn a silent background reload into the
// browser's native "Changes you made may not be saved" dialog, on a tab nobody was looking at —
// on a shared warehouse terminal, where the next operator finds a modal and no explanation. That
// is the exact inverse of what the reload is for.
//
// Nothing first-party registers one today. Tracked by wrapping the two window methods once, rather
// than by a registry each flow opts into, so the first flow that does register one is covered
// without either side knowing about the other.
//
// The hard part is not noticing registrations, it is noticing REMOVALS — the DOM has three ways to
// remove a listener and only one of them calls removeEventListener. A removal missed here leaves an
// entry behind forever, and since an entry means "do not reload", one missed removal disables the
// auto-reload for the rest of that tab's life, silently. All three are handled below:
// removeEventListener, `{ once: true }` (removed by the DOM when it fires), and `{ signal }`
// (removed when the signal aborts).
//
// Keyed by listener and never counted, because registering the same listener twice is a no-op for
// the DOM and has to be a no-op here too — otherwise a single unbalanced pair would leave a count
// permanently above zero, which is the same permanent latch by another route.
//
// Known limit, shared by `once` and `signal`: only the LAST registration of a given listener is
// remembered, so registering one function twice with different options and then triggering the
// earlier one's removal condition un-tracks a listener that is in fact still installed. Fixing that
// needs a count per listener, which trades this failure for the worse one above.
//
// listener -> { once, signal } of the registration that last won.
const registrations = new Map()

const INSTALLED_FLAG = '__cubboUnloadGuardsInstalled'

// The unwrapped methods, so this file can register a listener of its own without tracking it. Left
// unset if something installed before this copy, in which case it does no bookkeeping at all.
let nativeAdd = null
let nativeRemove = null

// `once` and `signal` only in the options-object form. The boolean third argument is the legacy
// capture flag and says nothing about either.
const readOptions = options => {
  const isObject = Boolean(options) && typeof options === 'object'
  return {
    once: isObject && Boolean(options.once),
    signal: isObject ? options.signal : undefined,
  }
}

// Pruning `once` guards needs a beforeunload listener of this file's own, and holding one is not
// free: browsers use "this page has a beforeunload listener" as a back/forward-cache eligibility
// signal, and Chrome on Android and Firefox make the page ineligible while one is registered.
// Attaching it at import time would charge every tab that, in every session, app-wide, for
// bookkeeping only needed while a `once` guard is outstanding — which in this app today is never.
// So it comes and goes with the need for it.
let pruneListenerRegistered = false

const prune = () => {
  registrations.forEach((registration, listener) => {
    if (registration.once) registrations.delete(listener)
  })
  syncPruneListener()
}

// Declared as a function so `prune` and `track` can reach it before it is defined.
function syncPruneListener() {
  if (!nativeAdd || !nativeRemove) return

  let needed = false
  registrations.forEach(registration => {
    if (registration.once) needed = true
  })

  if (needed && !pruneListenerRegistered) {
    nativeAdd.call(window, 'beforeunload', prune)
    pruneListenerRegistered = true
  } else if (!needed && pruneListenerRegistered) {
    nativeRemove.call(window, 'beforeunload', prune)
    pruneListenerRegistered = false
  }
}

const untrack = listener => {
  registrations.delete(listener)
  syncPruneListener()
}

const track = (listener, options) => {
  const { once, signal } = readOptions(options)

  // An already-aborted signal makes addEventListener do nothing at all, so there is no listener
  // to track. Recording one anyway would be an immediate permanent latch over a registration that
  // never happened.
  if (signal && signal.aborted) return

  registrations.set(listener, { once, signal })

  // AbortController cleanup is the modern idiom, and it removes the listener without ever calling
  // removeEventListener. Checking the signal is still the current one keeps this consistent with
  // `once`: the last registration wins, so a signal aborting after its listener was re-registered
  // plainly must not un-track it.
  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        const current = registrations.get(listener)
        if (current && current.signal === signal) untrack(listener)
      },
      { once: true },
    )
  }

  syncPruneListener()
}

const install = () => {
  if (typeof window === 'undefined' || window[INSTALLED_FLAG]) return
  window[INSTALLED_FLAG] = true

  nativeAdd = window.addEventListener
  nativeRemove = window.removeEventListener

  // Only the bookkeeping is added; what the page does on unload is left exactly as it was. In
  // particular the caller's listener is never wrapped or replaced — registering the same listener
  // twice is a DOM no-op, and two different wrappers would turn that into the handler firing
  // twice.
  window.addEventListener = function (type, listener, options) {
    if (type === 'beforeunload' && listener) track(listener, options)
    return nativeAdd.call(this, type, listener, options)
  }
  window.removeEventListener = function (type, listener, options) {
    if (type === 'beforeunload' && listener) untrack(listener)
    return nativeRemove.call(this, type, listener, options)
  }
}

install()

// `window.onbeforeunload = fn` is the classic idiom and never passes through addEventListener, so
// the map cannot see it. No first-party code uses it today, so the exposure is third-party scripts
// (scanner and kiosk helpers live in the browser profile on warehouse terminals) and future code;
// the cost of missing one is the browser's native "Changes you made may not be saved" dialog raised
// on a tab nobody was looking at, which is the precise inverse of a silent reload.
export const hasUnloadGuards = () => registrations.size > 0 || typeof window.onbeforeunload === 'function'

// Tests only: the wrapping is module-level and permanent, so the bookkeeping has to be clearable
// between cases that register real listeners.
export const __resetUnloadGuards = () => {
  registrations.clear()
  syncPruneListener()
}

// Tests only. That no beforeunload listener is held while nothing needs one is the entire point of
// attaching it lazily, and it is not observable through any DOM API.
export const __hasPruneListener = () => pruneListenerRegistered
